import type { SentryClientInstance, SentryEvent } from '../declarations/Clients';
import { globalObservability } from '../global';
import { trackErrorInDatadog } from './datadog';

type ScopeState = {
  extras: Record<string, unknown>;
};

const createScope = () => {
  const state: ScopeState = { extras: {} };
  return {
    state,
    scope: {
      setFingerprint() {},
      setUser() {},
      setTag() {},
      setTags() {},
      setExtra(key: string, value: unknown) {
        state.extras[key] = value;
      },
      setExtras(extras: Record<string, unknown>) {
        Object.assign(state.extras, extras);
      },
      setContext() {},
    },
  };
};

const asError = (exception: unknown, fallback: string): Error => {
  if (exception instanceof Error) {
    return exception;
  }
  if (typeof exception === 'string' && exception.length > 0) {
    return new Error(exception);
  }
  return new Error(fallback);
};

const diagnosticContext = (data: Record<string, unknown>) => ({
  level: 'warning',
  ...data,
});

/**
 * Used when Sentry is not selected. Direct `getSentryClient()` reports still have a
 * destination: errors go to Datadog RUM, and warnings stay diagnostics.
 */
export function datadogFallbackSentryClient(): SentryClientInstance {
  let active: ScopeState | undefined;

  const contextData = (eventExtra?: Record<string, unknown>): Record<string, unknown> => ({
    ...active?.extras,
    ...eventExtra,
  });

  const reportDiagnostic = (message: string, data: Record<string, unknown>) => {
    if (!globalObservability.capturesBrowserError('datadog')) {
      return;
    }
    globalObservability.getDatadogRumClient().addAction?.(message, diagnosticContext(data));
  };

  return {
    withScope(callback) {
      const { state, scope } = createScope();
      const previous = active;
      active = state;
      try {
        callback(scope);
      } finally {
        active = previous;
      }
    },
    captureException(exception) {
      const error = asError(exception, 'browser-error');
      trackErrorInDatadog(error.message, error, { data: contextData() });
      return undefined;
    },
    captureMessage(message) {
      reportDiagnostic(message, contextData());
      return undefined;
    },
    captureEvent(event: SentryEvent) {
      const message = event.message ?? 'browser-error';
      const data = contextData(event.extra);
      if (event.level === 'error' || event.level === 'fatal') {
        trackErrorInDatadog(message, new Error(message), { data });
        return undefined;
      }
      reportDiagnostic(message, { ...data, level: event.level ?? 'warning' });
      return undefined;
    },
  };
}
